import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../Api_Module/Api_Services/AuthService';

interface TimelineStep {
    title: string;
    status: 'pending' | 'processing' | 'success' | 'failure';
}

const Subscription = () => {
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(window.location.search);
    const trans:any = queryParams.get('session_id');

    const [transactionStatus, setTransactionStatus] = useState<'processing' | 'success' | 'failure' | null>(null);
    const [isPolling, setIsPolling] = useState(false);
    const [subscriptionConfig, setSubscriptionConfig] = useState<any>(null);

    useEffect(() => {
        if (trans) {
            console.log('Transaction ID:', trans);
            startPolling();
        }
        getSubscriptionConfig();
    }, [trans]);

    const getSubscriptionConfig = async () => {
        try {
            const response = await AuthService.getSubscriptionConfig();
            if (response) {
                setSubscriptionConfig(response);
            }
        } catch (error) {
            console.error('Error fetching subscription config:', error);
        }
    };

    const startPolling = () => {
        setIsPolling(true);
        setTransactionStatus('processing');
        pollTransaction();
    };

    const pollTransaction = async () => {
        const pollInterval = setInterval(async () => {
            try {
                const result = await AuthService.verifyTransaction(trans);
                if (result === 'paid' || result === 'unpaid') {
                    setTransactionStatus(result === "paid" ? "success" : "failure");
                    setIsPolling(false);
                    clearInterval(pollInterval);
                }
            } catch (error) {
                console.error('Error verifying transaction:', error);
                setTransactionStatus('failure');
                setIsPolling(false);
                clearInterval(pollInterval);
            }
        }, 5000);

        // Stop polling after 5 minutes
        setTimeout(() => {
            clearInterval(pollInterval);
            if (isPolling) {
                setTransactionStatus('failure');
                setIsPolling(false);
            }
        }, 300000);
    };

    const getTimelineSteps = (): TimelineStep[] => {
        return [
            {
                title: 'Payment Processing',
                status: transactionStatus === null ? 'pending' : 
                       transactionStatus === 'processing' ? 'processing' : 'success'
            },
            {
                title: transactionStatus === 'success' ? 'Payment Successful' : 
                       transactionStatus === 'failure' ? 'Payment Failed' : 'Awaiting Confirmation',
                status: transactionStatus === null || transactionStatus === 'processing' ? 'pending' : transactionStatus
            }
        ];
    };

    const getStepIcon = (status: string) => {
        switch (status) {
            case 'processing':
                return <div className="animate-spin w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full"></div>;
            case 'success':
                return <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-theme-primary text-sm font-bold">✓</div>;
            case 'failure':
                return <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-theme-primary text-sm font-bold">✗</div>;
            default:
                return <div className="w-6 h-6 bg-gray-600 rounded-full border-2 border-gray-500"></div>;
        }
    };

    const handleGoToDashboard = () => {
        navigate('/dashboard');
    };

    const handleGoToPricing = () => {
        navigate('/pricing');
    };

    return (
        <div className="py-8 px-4">
            <div className="max-w-lg mx-auto">
                <div className="bg-theme-secondary rounded-2xl shadow-2xl border border-theme-tertiary overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6">
                        <h2 className="text-2xl font-bold text-theme-primary text-center">Payment Status</h2>
                        <p className="text-blue-100 text-center mt-1">Subscription Verification</p>
                    </div>

                    {/* Content */}
                    <div className="px-8 py-6">

                        {/* Timeline */}
                        <div className="relative">
                            {getTimelineSteps().map((step, index) => (
                                <div key={index} className="flex items-start mb-8 last:mb-6">
                                    <div className="flex flex-col items-center mr-6">
                                        {getStepIcon(step.status)}
                                        {index < getTimelineSteps().length - 1 && (
                                            <div className={`w-0.5 h-12 mt-3 ${
                                                step.status === 'success' ? 'bg-green-400' : 
                                                step.status === 'processing' ? 'bg-blue-400' : 'bg-gray-600'
                                            }`}></div>
                                        )}
                                    </div>
                                    <div className="flex-1 pt-1">
                                        <h3 className={`font-semibold text-lg ${
                                            step.status === 'success' ? 'text-green-400' :
                                            step.status === 'failure' ? 'text-red-400' :
                                            step.status === 'processing' ? 'text-blue-400' : 'text-theme-muted'
                                        }`}>
                                            {step.title}
                                        </h3>
                                        {step.status === 'processing' && (
                                            <div className="mt-2">
                                                <p className="text-theme-secondary text-sm">
                                                    Please wait while we verify your payment...
                                                </p>
                                                <div className="flex items-center mt-2">
                                                    <div className="flex space-x-1">
                                                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                                                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                                                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                                                    </div>
                                                    <span className="text-xs text-theme-muted ml-2">
                                                        Processing
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Status Messages */}
                        {transactionStatus === 'failure' && (
                            <div className="mt-6 p-6 bg-red-900/30 border-l-4 border-red-500 rounded-r-lg backdrop-blur-sm">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                                            <span className="text-red-400 font-bold">!</span>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <h4 className="text-red-300 font-semibold">
                                            Payment Failed
                                        </h4>
                                        <p className="text-red-400 text-sm mt-1">
                                            Your payment could not be processed. Please try again or contact our support team.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {transactionStatus === 'success' && (
                            <div className="mt-6 p-6 bg-green-900/30 border-l-4 border-green-500 rounded-r-lg backdrop-blur-sm">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                                            <span className="text-green-400 font-bold">✓</span>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <h4 className="text-green-300 font-semibold">
                                            Payment Successful!
                                        </h4>
                                        <p className="text-green-400 text-sm mt-1">
                                            Welcome to Stock Fortress! Your subscription is now active and ready to use.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Dashboard Button */}
                        {transactionStatus === 'success' && (
                            <div className="mt-8 flex justify-center">
                                <button
                                    onClick={handleGoToDashboard}
                                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-theme-primary font-semibold rounded-lg shadow-lg transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                                >
                                    Go to Dashboard
                                </button>
                            </div>
                        )}

                        {transactionStatus === 'failure' && (
                            <div className="mt-8 flex justify-center">
                                <button
                                    onClick={handleGoToPricing}
                                    className="px-8 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-theme-primary font-semibold rounded-lg shadow-lg transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                                >
                                    Back to Pricing
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Subscription;
